import { Link } from "@tanstack/react-router";
import { MapPin, Phone, ArrowUpRight } from "lucide-react";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { InstallAppButton } from "@/components/InstallAppButton";
import { FooterAds } from "@/components/AdSlot";

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border bg-secondary/40 pb-20 lg:pb-0">
      <FooterAds />
      <div className="container-edge border-b border-border py-14 md:py-16">
        <div className="grid gap-8 md:grid-cols-12 md:items-center">
          <div className="md:col-span-6">
            <p className="eyebrow">Nyhetsbrev</p>
            <h2 className="mt-3 font-display text-3xl leading-tight md:text-4xl">
              Nya husbilar, guider &amp; <span className="font-italic-display text-accent">rese­tips</span>
            </h2>
            <p className="mt-3 max-w-md text-sm text-muted-foreground md:text-base">
              Cirka ett mejl i månaden — aldrig spam, du kan avanmäla dig när du vill.
            </p>
          </div>
          <div className="md:col-span-6">
            <NewsletterSignup
              variant="footer"
              source="footer"
              eyebrow=""
              title=""
              description=""
            />
          </div>
        </div>
      </div>
      <div className="container-edge grid gap-12 py-16 md:grid-cols-12 md:py-20">
        <div className="md:col-span-5">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-display text-3xl leading-none">AK Husbil</span>
            <span className="font-italic-display text-2xl leading-none text-accent">&amp; Fritid</span>
          </Link>
          <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
            Familjeägt sedan 2004. Kontrollerade husbilar, husvagnar och tillbehör — med personlig service från Kalix.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <a href="tel:0708629743" className="flex items-center gap-2 text-foreground hover:text-accent">
              <Phone size={16} className="text-accent" /> 070-862 97 43 · Aaron
            </a>
            <a href="tel:0703045344" className="flex items-center gap-2 text-foreground hover:text-accent">
              <Phone size={16} className="text-accent" /> 070-304 53 44 · Inköp
            </a>
            <p className="flex items-start gap-2 text-foreground">
              <MapPin size={16} className="mt-0.5 text-accent" /> Stabsvägen 5, 952 51 Kalix
            </p>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow">Sajten</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[
              { to: "/fordon", label: "Fordon" },
              { to: "/marknad", label: "Marknadsdata" },
              { to: "/prisindex", label: "AK Prisindex" },
              { to: "/borsen", label: "Fritidsbörsen" },
              { to: "/vardera-husbil", label: "Värdera fordon" },
              { to: "/fritid", label: "Fritid" },
              { to: "/reservdelar", label: "Reservdelar" },
              { to: "/boka-service", label: "Boka verkstad" },
              { to: "/omdomen", label: "Omdömen" },
              { to: "/guider", label: "Guider" },
              { to: "/campingtips", label: "Campingtips" },
              { to: "/blogg", label: "Blogg" },
              { to: "/om-oss", label: "Om oss" },
              { to: "/kontakt", label: "Kontakt" },
            ].map((l) => (

              <li key={l.to}>
                <Link to={l.to} className="text-foreground/80 transition hover:text-accent">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="eyebrow">Köpa husbil</p>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {[
              { to: "/husbilar-till-salu", label: "Husbilar till salu" },
              { to: "/husvagnar-till-salu", label: "Husvagnar till salu" },
              { to: "/husbil-matchning", label: "Hitta rätt husbil" },
              { to: "/rapido", label: "Rapido" },
              { to: "/jamfor", label: "Jämförelser" },
              { to: "/modeller", label: "Modellguider" },
              { to: "/ordlista", label: "Ordlista" },
              { to: "/fakta", label: "Fakta" },
              { to: "/husbil-kalix", label: "Husbil Kalix" },
              { to: "/husbil-lulea", label: "Husbil Luleå" },
              { to: "/husbil-pitea", label: "Husbil Piteå" },
              { to: "/husbil-boden", label: "Husbil Boden" },
              { to: "/husbil-norrbotten", label: "Husbil Norrbotten" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-foreground/80 transition hover:text-accent">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <p className="eyebrow mt-8">Se vårt utbud</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <a href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-foreground/80 hover:text-accent">
                Fyndanu <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </li>
            <li>
              <a href="https://www.blocket.se/butik/ak-husbil--fritid" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-foreground/80 hover:text-accent">
                Blocket <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </li>
          </ul>
        </div>

      </div>

      <div className="border-t border-border">
        <div className="container-edge flex flex-col items-center justify-between gap-4 py-6 text-xs text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} AK Husbil &amp; Fritid</p>
          <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
            <li><Link to="/integritetspolicy" className="hover:text-accent">Integritetspolicy</Link></li>
            <li><Link to="/cookies" className="hover:text-accent">Cookies</Link></li>
            <li><Link to="/anvandarvillkor" className="hover:text-accent">Användarvillkor</Link></li>
            <li><Link to="/kopvillkor" className="hover:text-accent">Köpvillkor &amp; returer</Link></li>
            <li><Link to="/erfarenheter" className="hover:text-accent">Fukt- &amp; gasolrapporter</Link></li>
            <li><Link to="/webbkarta" className="hover:text-accent">Webbkarta</Link></li>
            <li><Link to="/fi" className="hover:text-accent" hrefLang="fi">Suomeksi</Link></li>
          </ul>
          <div className="flex items-center gap-4">
            <InstallAppButton />
            <p>Byggt i Norrbotten</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
